import { TrendIconEnum } from '@/components/atoms/TrendIcon/TrendIconUtils';
import { formatNumberByLocale } from '@/utils/localeFormatters';

import type { LiceInMin200Fish } from './types';

export type LiceType = 'female' | 'movingStage' | 'caligusElongatus' | 'ovigerousFemale';

/** Above this many lice per fish the exact number stops being useful and `>10` is shown. */
const CROWDED_THRESHOLD = 10;
const DISPLAYED_DECIMALS = 2;
const NO_VALUE = '-';

/**
 * Which counts the block shows.
 *
 * Chile tracks no Caligus elongatus and reports the ovigerous female rather than the mature
 * female. Exported because the loading placeholder has to draw the same number of rows — one
 * row out and the panel resizes the moment the data lands.
 */
export function getLiceTypes(isChileanLice: boolean): LiceType[] {
    return isChileanLice
        ? ['ovigerousFemale', 'movingStage']
        : ['female', 'movingStage', 'caligusElongatus'];
}

/** The ovigerous female is the mature female count under another name, not a separate field. */
const SOURCE_FIELD: Record<LiceType, 'female' | 'movingStage' | 'caligusElongatus'> = {
    female: 'female',
    ovigerousFemale: 'female',
    movingStage: 'movingStage',
    caligusElongatus: 'caligusElongatus',
};

const LABEL_KEY: Record<LiceType, string> = {
    female: 'cageLice.common.matureFemaleLice',
    ovigerousFemale: 'common.labels.ovigerousFemale',
    movingStage: 'common.labels.mobileLice',
    caligusElongatus: 'common.labels.caligusLice',
};

/** What the count means, shown in the tooltip whatever the value is. */
const EXPLANATION_KEY: Record<LiceType, string> = {
    female: 'liceNumberHelpText.adult',
    ovigerousFemale: 'liceNumberHelpText.ovigerousFemale',
    movingStage: 'liceNumberHelpText.mobile',
    caligusElongatus: 'liceNumberHelpText.caligus',
};

/** Added to the tooltip only once the count is shown as `>10`, to say what the cap means. */
const CROWDED_KEY: Record<LiceType, string> = {
    female: 'molecules.liceStatus.moreThan10AdultFemale',
    ovigerousFemale: 'molecules.liceStatus.moreThan10OvigerousFemale',
    movingStage: 'molecules.liceStatus.moreThan10Mobile',
    caligusElongatus: 'molecules.liceStatus.moreThan10Caligus',
};

/** A count as the block draws it. Everything is already translated and formatted. */
export interface LiceStatusRow {
    liceType: LiceType;
    label: string;
    /** The formatted number, `>10`, or `-` when there is nothing to show. */
    value: string;
    trend: TrendIconEnum;
    /** Tooltip paragraphs, in order. Empty when there is nothing to explain. */
    tooltip: string[];
}

/** More lice than last week is bad news, so the arrow's colour is inverted against the sign. */
function trendFor(difference: number | null | undefined): TrendIconEnum {
    if (difference === null || difference === undefined) return TrendIconEnum.TrendNone;
    if (difference > 0) return TrendIconEnum.TrendNegativeUp;
    if (difference < 0) return TrendIconEnum.TrendPositiveDown;
    return TrendIconEnum.TrendNone;
}

/** The translations are prose with sentences separated by `. `, rendered a paragraph each. */
function toParagraphs(text: string): string[] {
    return text
        .split('. ')
        .map((sentence) => sentence.trim())
        .filter(Boolean)
        .map((sentence) => (sentence.endsWith('.') ? sentence : `${sentence}.`));
}

export interface BuildLiceStatusRowsParams {
    liceInMin200Fish: LiceInMin200Fish | null | undefined;
    isChileanLice: boolean;
    locale: string;
    t: (key: string) => string;
}

/**
 * The rows the block renders, derived in one pass.
 *
 * Pure and outside the component: this is where every decision about what a count *reads* as
 * lives, so it can be checked without rendering anything.
 */
export function buildLiceStatusRows(params: BuildLiceStatusRowsParams): LiceStatusRow[] {
    const { liceInMin200Fish, isChileanLice, locale, t } = params;

    return getLiceTypes(isChileanLice).map((liceType) => {
        const field = SOURCE_FIELD[liceType];
        const value = liceInMin200Fish?.[`${field}Avg`];
        const label = t(LABEL_KEY[liceType]);
        const trend = trendFor(liceInMin200Fish?.[`${field}AvgDiff`]);

        if (value === null || value === undefined) {
            return {
                liceType,
                label,
                value: NO_VALUE,
                trend,
                tooltip: [t('molecules.liceStatus.notEnoughMeasurements')],
            };
        }

        const crowded = value > CROWDED_THRESHOLD;

        return {
            liceType,
            label,
            value: crowded
                ? '>10'
                : formatNumberByLocale(value, locale, DISPLAYED_DECIMALS, NO_VALUE),
            trend,
            tooltip: [
                ...toParagraphs(t(EXPLANATION_KEY[liceType])),
                ...(crowded ? toParagraphs(t(CROWDED_KEY[liceType])) : []),
            ],
        };
    });
}
